import {
  handleResp,
  getURLWithQueryParams,
  getDefaultHeaders,
  extendFetchOptions,
} from "./utils";
import type {
  WrappedCashifyFunction,
  WrappedCashifyPageableQuery,
  CommonResponse,
  GenericResponse,
  Sortable,
} from "./utils";
import type { AdminGroupDetail } from "./admin-group-service";

export interface AdminService {
  listAdmin: ReturnType<typeof listAdmin>;
  postAdmin: ReturnType<typeof postAdmin>;
  getAdminDetail: ReturnType<typeof getAdminDetail>;
  putAdminDetail: ReturnType<typeof putAdminDetail>;
  deleteAdminDetail: ReturnType<typeof deleteAdminDetail>;
  putAdminStatus: ReturnType<typeof putAdminStatus>;
  listAdminRole: ReturnType<typeof listAdminRole>;
};

// Admin
export interface AdminSearchCriteria extends Sortable {
  id?: number;
  username?: string;
  adminName?: string;
  "lastPasswordUpdateTime.from"?: string;
  "lastPasswordUpdateTime.to"?: string;
  contactEmail?: string;
  status?: AdminStatus[];
  "lastLoginTime.from"?: string;
  "lastLoginTime.to"?: string;
  emergencyAccount?: boolean;
  "createTime.from"?: string;
  "createTime.to"?: string;
  createAdmin?: string;
  "lastUpdateTime.from"?: string;
  "lastUpdateTime.to"?: string;
  lastUpdateAdmin?: string;
  withGroupId?: number;
};

export type AdminStatus = "ACTIVE" | "DISABLED" | "LOCKED";

export interface AdminDTO {
  id: number;
  tenantId: number;
  username: string;
  adminName: string;
  password?: string;
  changePasswordRequired?: boolean;
  lastPasswordUpdateTime?: string;
  contactEmail?: string;
  status?: AdminStatus;
  failedLoginCount?: number;
  lastLoginIp?: string;
  lastLoginTime?: string;
  emergencyAccount?: boolean;
  mfaEnabled?: boolean;
  createTime?: string;
  createAdmin?: string;
  lastUpdateTime?: string;
  lastUpdateAdmin?: string;
};

export interface AdminDetail extends AdminDTO {
  groups?: AdminGroupDetail[];
  roleIds?: number[];
};

export interface CreateAdminRequest {
  username: string;
  adminName: string;
  password?: string;
  changePasswordRequired?: boolean;
  contactEmail?: string;
  emergencyAccount?: boolean;
  groupIds?: number[];
  roleIds?: number[];
};

export interface UpdateAdminRequest {
  id: number;
  detail: { passwordChanged?: boolean } & CreateAdminRequest;
};

// Admin Role
export interface AdminRoleDTO {
  id: number;
  code: string;
  order: number;
  parentRoleId?: number;
  requiredRoleId?: number;
};

export const getAdminDetail: WrappedCashifyFunction<{ id: number }, GenericResponse<AdminDetail>> = ({ urlPrefix, options }) => {
  return async ({ id }) => {
    let url = `${urlPrefix}/api/v1/admin/${id}`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const postAdmin: WrappedCashifyFunction<CreateAdminRequest, CommonResponse> = ({ urlPrefix, options }) => {
  return async (request) => {
    let url = `${urlPrefix}/api/v1/admin`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putAdminDetail: WrappedCashifyFunction<UpdateAdminRequest, CommonResponse> = ({ urlPrefix, options }) => {
  return async ({ id, detail }) => {
    let url = `${urlPrefix}/api/v1/admin/${id}`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(detail),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const deleteAdminDetail: WrappedCashifyFunction<{ id: number }, CommonResponse> = ({ urlPrefix, options }) => {
  return ({ id }) => {
    let url = `${urlPrefix}/api/v1/admin/${id}`;
    let opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putAdminStatus: WrappedCashifyFunction<{ id: number, status: AdminStatus }, CommonResponse> = ({ urlPrefix, options }) => {
  return async ({ id, status }) => {
    let url = `${urlPrefix}/api/v1/admin/${id}/status`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const listAdmin: WrappedCashifyPageableQuery<AdminSearchCriteria, AdminDTO> = ({ urlPrefix, options }) => {
  return async (queryParams) => {
    let url = getURLWithQueryParams(`${urlPrefix}/api/v1/admin`, queryParams);
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const listAdminRole: WrappedCashifyFunction<{}, GenericResponse<AdminRoleDTO[]>> = ({ urlPrefix, options }) => {
  return async () => {
    let url = `${urlPrefix}/api/v1/admin-role`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
